(() => {
  const chat = document.querySelector('#chat-04');
  const host = document.querySelector('[data-chat-moss-04]');
  if (!chat || !host) return;

  let layer = null;
  let canvas = null;
  let raf = 0;
  let ro = null;
  let lastKey = '';
  let timers = [];

  const viewport = () => {
    const vv = window.visualViewport;
    return {
      left: vv?.offsetLeft || 0,
      top: vv?.offsetTop || 0,
      width: vv?.width || window.innerWidth,
      height: vv?.height || window.innerHeight
    };
  };

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  const visibleIntersection = (rect, vp) => {
    const left = Math.max(rect.left, vp.left);
    const top = Math.max(rect.top, vp.top);
    const right = Math.min(rect.right, vp.left + vp.width);
    const bottom = Math.min(rect.bottom, vp.top + vp.height);
    return {
      left,
      top,
      right,
      bottom,
      width: Math.max(0, right - left),
      height: Math.max(0, bottom - top)
    };
  };

  const shadowCandidates = () => {
    const out = [];
    const visit = (root) => {
      if (!root?.querySelectorAll) return;
      root.querySelectorAll('iframe, [role="dialog"], [part*="container"], [part*="panel"], [part*="window"]').forEach((el) => {
        const rect = el.getBoundingClientRect?.();
        if (rect) out.push(rect);
        if (el.shadowRoot) visit(el.shadowRoot);
      });
      root.querySelectorAll('*').forEach((el) => {
        if (el.shadowRoot) visit(el.shadowRoot);
      });
    };
    if (chat.shadowRoot) visit(chat.shadowRoot);
    return out;
  };

  const actualChatRect = () => {
    const vp = viewport();
    const candidates = [chat.getBoundingClientRect(), ...shadowCandidates()]
      .map((rect) => visibleIntersection(rect, vp))
      .filter((rect) =>
        rect.width >= 220 &&
        rect.height >= 180 &&
        rect.width <= vp.width + 2 &&
        rect.height <= vp.height + 2
      );

    if (!candidates.length) return null;

    // Prefer the visible chat panel closest to the bottom-right and with useful area.
    candidates.sort((a, b) => {
      const aScore = (a.width * a.height) + a.bottom * 20 + a.right * 10;
      const bScore = (b.width * b.height) + b.bottom * 20 + b.right * 10;
      return bScore - aScore;
    });

    return candidates[0];
  };

  const fallbackRect = () => {
    const vp = viewport();
    const mobile = vp.width < 750;
    const gap = mobile ? 8 : 16;
    const width = mobile
      ? Math.max(240, vp.width - gap * 2)
      : Math.min(390, Math.max(320, vp.width - gap * 2));
    const panelHeight = mobile
      ? clamp(vp.height * 0.42, 170, 260)
      : clamp(vp.height * 0.34, 190, 280);

    return {
      left: vp.left + vp.width - width - gap,
      top: vp.top + vp.height - panelHeight - gap,
      width,
      height: panelHeight
    };
  };

  const mossRect = () => {
    const vp = viewport();
    const rect = actualChatRect();
    if (!rect) return fallbackRect();

    const mobile = vp.width < 750;
    const zoneHeight = clamp(
      rect.height * (mobile ? 0.46 : 0.5),
      mobile ? 150 : 170,
      mobile ? 250 : 290
    );

    const width = clamp(rect.width, Math.min(240, vp.width), vp.width);
    const left = clamp(rect.left, vp.left, vp.left + vp.width - width);
    const bottom = clamp(rect.bottom, vp.top + zoneHeight, vp.top + vp.height);

    return {
      left,
      top: bottom - zoneHeight,
      width,
      height: zoneHeight
    };
  };

  const clearTimers = () => {
    timers.forEach(clearTimeout);
    timers = [];
  };

  const rebuild = () => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      const open = chat.hasAttribute('open') || document.documentElement.classList.contains('chat04-open');

      if (!open) {
        host.style.display = 'none';
        lastKey = '';
        return;
      }

      const engine = window.Moss04Engine;
      if (!engine?.Layer) return;

      const rect = mossRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const key = [
        Math.round(rect.left),
        Math.round(rect.top),
        Math.round(rect.width),
        Math.round(rect.height),
        Math.round(dpr * 100)
      ].join(':');

      host.style.display = 'block';
      host.style.left = rect.left + 'px';
      host.style.top = rect.top + 'px';
      host.style.width = rect.width + 'px';
      host.style.height = rect.height + 'px';

      if (!canvas) {
        canvas = document.createElement('canvas');
        canvas.setAttribute('aria-hidden', 'true');
        host.appendChild(canvas);
      }

      if (key === lastKey && layer) return;
      lastKey = key;

      if (layer?.raf) cancelAnimationFrame(layer.raf);

      layer = new engine.Layer(host, {
        canvas,
        lut: engine.makeLUT(engine.DEF),
        size: rect.width < 390 ? 0.82 : 0.92,
        dir: 'up',
        anchor: 'top',
        height: (height) => height,
        profile: (t) => 0.16 + 0.84 * engine.ss(0, 1, t)
      });

      layer.build();
      layer.paint(rect.width < 390 ? 0.88 : 0.94, null);
    });
  };

  const settle = () => {
    clearTimers();
    rebuild();
    [70, 180, 360, 700].forEach((delay) => {
      timers.push(setTimeout(rebuild, delay));
    });
  };

  const waitForEngine = () => {
    if (window.Moss04Engine?.Layer) {
      settle();
      return;
    }
    setTimeout(waitForEngine, 60);
  };

  new MutationObserver(settle).observe(chat, {
    attributes: true,
    attributeFilter: ['open', 'mode', 'style', 'class']
  });

  window.addEventListener('resize', settle, { passive: true });
  window.addEventListener('orientationchange', settle, { passive: true });

  if (window.visualViewport) {
    visualViewport.addEventListener('resize', settle, { passive: true });
    visualViewport.addEventListener('scroll', rebuild, { passive: true });
  }

  document.addEventListener('shopify-chat:open', settle);
  document.addEventListener('shopify-chat:close', rebuild);

  if ('ResizeObserver' in window) {
    ro = new ResizeObserver(settle);
    ro.observe(chat);
  }

  waitForEngine();
})();